import { useEffect, useCallback, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import getSymbolFromCurrency from "currency-symbol-map";
import { Globe, Lock } from "lucide-react";

// Contexts
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { useAuth } from "@/contexts/AuthContext";
// UI components
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { Form } from "@/components/ui/form";
import { LoadingButtonContent } from "../ui/loading-button-content";
import { Switch } from "@/components/ui/switch";

// Misc libraries
import { supabase } from "@/lib/supabaseClient";
import { PricePresentation } from "@/types/Products";

// Form input components
import { CostSlider } from "./edit-product-dialog/CostSlider";
import { PriceRangeSlider } from "./edit-product-dialog/PriceRangeSlider";
import { PriceRoundSelect } from "./edit-product-dialog/PriceRoundSelect";
import { MetricSelect } from "./edit-product-dialog/MetricSelect";

const formSchema = z.object({
    minPrice: z.number(),
    maxPrice: z.number(),
    markup: z.number(),
    cost: z.number().min(0).max(1000000),
    optimizationInterval: z.number(),
    timezone: z.string(),
    market: z.string(),
    metric: z.enum(["dgp", "ppv"]),
    presentation: z.custom<PricePresentation>(),
});

export default function SingleEditProductDialog({
    product,
    markets,
    isOpen,
    onClose,
}: {
    product: any;
    markets: any[];
    isOpen: boolean;
    onClose: () => void;
}) {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [selectedMarket, setSelectedMarket] = useState("all");
    const [enabledMarkets, setEnabledMarkets] = useState(
        product.optimization_settings?.enabled_markets || []
    );
    const [
        productPriceInSelectedMarketCurrency,
        setProductPriceInSelectedMarketCurrency,
    ] = useState(product.price);
    const [exchangeRates, setExchangeRates] = useState<Record<string, number>>(
        {}
    );
    const [optimizationSettings, setOptimizationSettings] = useState(
        product?.optimization_settings
    );
    const { shop } = useAuth();

    const [selectedMarketCurrencyCode, setSelectedMarketCurrencyCode] =
        useState(shop?.base_currency || "GBP");

    const isOptimizationUnderway = useMemo(() => {
        return product.status === "Optimizing";
    }, [product]);

    // Function to convert price between currencies
    const convertPrice = useCallback(
        (amount: number, fromCurrency: string, toCurrency: string) => {
            if (fromCurrency === toCurrency) return amount;
            if (!exchangeRates || Object.keys(exchangeRates).length === 0)
                return amount;

            // Convert from source currency to GBP
            const fromRate = exchangeRates[fromCurrency] || 1;
            const toRate = exchangeRates[toCurrency] || 1;

            // Convert from source to target currency
            return (amount / fromRate) * toRate;
        },
        [exchangeRates]
    );

    // Fetch exchange rates when component mounts
    useEffect(() => {
        const fetchExchangeRates = async () => {
            try {
                const { data, error } = await supabase
                    .from("metadata")
                    .select("*")
                    .eq("key", "exchange_rates")
                    .single();

                if (error) {
                    console.error("Error fetching exchange rates:", error);
                } else if (data && data.value) {
                    setExchangeRates(data.value);
                }
            } catch (err) {
                console.error("Failed to fetch exchange rates:", err);
            }
        };

        fetchExchangeRates();
    }, []);

    // Update optimization settings when product.optimization_settings changes
    useEffect(() => {
        setOptimizationSettings(product?.optimization_settings);
    }, [product?.optimization_settings]);

    let product_cost = product.cost || product.price / 5;
    if (product.shopify_unit_cost) {
        product_cost = product.shopify_unit_cost;
    }

    // Helper function to remove undefined values from an object
    const removeUndefinedValues = useCallback((obj: any): any => {
        if (obj === null || obj === undefined) {
            return obj;
        }
        if (Array.isArray(obj)) {
            return obj.map((item) => removeUndefinedValues(item));
        }
        if (typeof obj === "object") {
            const result: any = {};
            for (const [key, value] of Object.entries(obj)) {
                if (value !== undefined) {
                    result[key] = removeUndefinedValues(value);
                }
            }
            return result;
        }
        return obj;
    }, []);

    const formHasChanged = useCallback(() => {
        const enabledMarketsChanged =
            JSON.stringify(
                [...(optimizationSettings?.enabled_markets || [])].sort()
            ) !== JSON.stringify([...enabledMarkets].sort());
        const optimizationSettingsChanged =
            JSON.stringify(removeUndefinedValues(optimizationSettings)) !==
            JSON.stringify(
                removeUndefinedValues(product?.optimization_settings)
            );
        return enabledMarketsChanged || optimizationSettingsChanged;
    }, [
        optimizationSettings,
        enabledMarkets,
        product?.optimization_settings,
        removeUndefinedValues,
    ]);

    const undoChanges = useCallback(() => {
        // Set enabledMarkets to the product.optimization_settings.enabled_markets
        setEnabledMarkets(
            product?.optimization_settings?.enabled_markets || []
        );
        // Set optimizationSettings to the product.optimization_settings
        setOptimizationSettings(product?.optimization_settings);
    }, [product?.optimization_settings]);

    const defaultValues = {
        minPrice: product.min_price || 0.75 * product.price,
        maxPrice: product.max_price || 1.25 * product.price,
        markup: product.markup || 5,
        cost: product_cost,
        priceRounding: product.price_rounding,
        optimizationInterval: product.optimization_interval || 86400,
        timezone: product.timezone || "Europe/London",
        market: product.market || "all",
        metric: product.metric || "dgp",
    };

    const recalculateFormValues = useCallback(() => {
        const baseCurrencyCode = optimizationSettings?.base_market?.currency;
        let minPrice = optimizationSettings?.base_market?.min_price;
        let maxPrice = optimizationSettings?.base_market?.max_price;
        let cost = optimizationSettings?.base_market?.cost;
        let presentation = optimizationSettings?.base_market?.presentation;
        if (selectedMarket !== "all") {
            const market = optimizationSettings?.markets?.[selectedMarket];
            const marketCurrencyCode = markets.find(
                (m) => m.handle === selectedMarket
            )?.base_currency;
            // Set the product price in the selected market currency
            setProductPriceInSelectedMarketCurrency(
                convertPrice(
                    product.price,
                    baseCurrencyCode,
                    marketCurrencyCode
                )
            );

            if (market) {
                if (market?.min_price) {
                    minPrice = convertPrice(
                        market?.min_price,
                        marketCurrencyCode,
                        baseCurrencyCode
                    );
                }
                if (market?.max_price) {
                    maxPrice = convertPrice(
                        market?.max_price,
                        marketCurrencyCode,
                        baseCurrencyCode
                    );
                }
                if (market?.cost) {
                    cost = convertPrice(
                        market?.cost,
                        marketCurrencyCode,
                        baseCurrencyCode
                    );
                }
                if (market?.presentation) {
                    presentation = market?.presentation;
                }
            }
        } else {
            setProductPriceInSelectedMarketCurrency(product.price);
        }

        form.setValue("market", selectedMarket);
        form.setValue(
            "minPrice",
            convertPrice(minPrice, baseCurrencyCode, selectedMarketCurrencyCode)
        );
        form.setValue(
            "maxPrice",
            convertPrice(maxPrice, baseCurrencyCode, selectedMarketCurrencyCode)
        );
        form.setValue(
            "cost",
            convertPrice(cost, baseCurrencyCode, selectedMarketCurrencyCode)
        );
        form.setValue("presentation", presentation);
    }, [selectedMarket, optimizationSettings, selectedMarketCurrencyCode]);

    useEffect(() => {
        recalculateFormValues();
    }, [selectedMarket, optimizationSettings, selectedMarketCurrencyCode]);

    const isMarketOverridden = useCallback(
        (market: string) => {
            // Check if the market is overridden by checking if the key is in the markets object of optimizationSettings and if it is, if it is empty.
            const marketSettings = optimizationSettings?.markets?.[market];
            if (!marketSettings) {
                return false;
            }
            // Might have been previously overridden but now not set.
            if (Object.keys(marketSettings).length === 0) {
                return false;
            }
            // Check if all the entries are undefined.
            const allUndefined = Object.values(marketSettings).every(
                (value) => value === undefined
            );
            if (allUndefined) {
                return false;
            }
            return true;
        },
        [optimizationSettings]
    );

    const form = useForm<z.infer<typeof formSchema>>({
        resolver: zodResolver(formSchema),
        defaultValues,
    });

    async function onSubmit(values: z.infer<typeof formSchema>) {
        setIsSubmitting(true);

        optimizationSettings.enabled_markets = enabledMarkets;

        const { error } = await supabase
            .from("products")
            .update({
                min_price: values.minPrice,
                max_price: values.maxPrice,
                markup: values.markup,
                cost: values.cost,
                price_rounding: values.priceRounding,
                optimization_interval: values.optimizationInterval,
                timezone: values.timezone,
                market: values.market,
                metric: values.metric,
                optimization_settings: optimizationSettings,
            })
            .eq("id", product.id);
        if (error) {
            console.error(error);
        }
        setIsSubmitting(false);
        form.reset(defaultValues);
        onClose();
    }

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="sm:max-w-[1200px]">
                <DialogHeader>
                    <DialogTitle>Edit product</DialogTitle>
                    <DialogDescription>
                        {isOptimizationUnderway ? (
                            <div className="flex items-center gap-2 text-yellow-600">
                                <Lock className="h-4 w-4" />
                                <span>
                                    Optimization is currently underway. Settings
                                    cannot be modified until it completes.
                                </span>
                            </div>
                        ) : (
                            "Change optimization options for this product."
                        )}
                    </DialogDescription>
                </DialogHeader>
                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)}>
                        <div className="flex">
                            {/* Market Sidebar */}
                            <div className="w-[220px] border-r pr-2 max-h-[60vh] overflow-y-auto relative flex flex-col">
                                <div className="space-y-1 py-2 flex-grow overflow-y-auto">
                                    <div
                                        className={`px-3 py-2 rounded-md cursor-pointer flex items-center justify-between text-sm
                                            ${
                                                selectedMarket === "all"
                                                    ? "bg-primary/10 text-primary font-medium"
                                                    : "hover:bg-muted"
                                            }`}
                                        onClick={() => {
                                            setSelectedMarket("all");
                                            setSelectedMarketCurrencyCode(
                                                shop?.base_currency || "GBP"
                                            );
                                        }}
                                    >
                                        <span className="flex items-center gap-2">
                                            <Globe className="h-4 w-4" />
                                            All Markets
                                        </span>
                                    </div>
                                    {markets.map((market) => (
                                        <div
                                            key={market.handle}
                                            className={`px-3 py-2 rounded-md cursor-pointer flex items-center justify-between text-sm
                                                ${
                                                    selectedMarket ===
                                                    market.handle
                                                        ? "bg-primary/10 text-primary font-medium"
                                                        : "hover:bg-muted"
                                                }`}
                                            onClick={() => {
                                                setSelectedMarket(
                                                    market.handle
                                                );
                                                setSelectedMarketCurrencyCode(
                                                    market.base_currency
                                                );
                                            }}
                                        >
                                            <span className="flex items-center gap-1">
                                                {market.name}
                                                {isMarketOverridden(
                                                    market.handle
                                                ) && (
                                                    <div className="ml-1 w-1.5 h-1.5 rounded-full bg-primary" />
                                                )}
                                            </span>
                                            <Switch
                                                checked={enabledMarkets.includes(
                                                    market.handle
                                                )}
                                                onCheckedChange={(checked) => {
                                                    setEnabledMarkets(
                                                        checked
                                                            ? [
                                                                  ...enabledMarkets,
                                                                  market.handle,
                                                              ]
                                                            : enabledMarkets.filter(
                                                                  (
                                                                      handle: string
                                                                  ) =>
                                                                      handle !==
                                                                      market.handle
                                                              )
                                                    );
                                                }}
                                                onClick={(e) =>
                                                    e.stopPropagation()
                                                }
                                                disabled={
                                                    isOptimizationUnderway
                                                }
                                                aria-label={`Toggle ${market.name} optimization`}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Main Content */}
                            <div className="flex-1 max-h-[60vh] overflow-y-scroll py-4 px-4 space-y-6 mb-4">
                                <div className="mb-4">
                                    <h3 className="font-medium flex items-center gap-2">
                                        {selectedMarket === "all" ? (
                                            <>
                                                <Globe className="h-4 w-4" />
                                                All Markets
                                            </>
                                        ) : (
                                            markets.find(
                                                (m) =>
                                                    m.handle === selectedMarket
                                            )?.name || "Selected Market"
                                        )}

                                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-sm font-medium border border-border text-muted-foreground">
                                            {(() => {
                                                const symbol =
                                                    getSymbolFromCurrency(
                                                        selectedMarketCurrencyCode
                                                    ) || "";
                                                return `${selectedMarketCurrencyCode} ${
                                                    symbol && `(${symbol})`
                                                }`;
                                            })()}
                                        </span>
                                        {selectedMarket === "all" && (
                                            <span className="text-xs font-normal ml-2">
                                                All prices will be converted to
                                                local currencies from{" "}
                                                {shop?.base_currency}
                                            </span>
                                        )}
                                        {selectedMarket !== "all" && (
                                            <div className="ml-auto text-right">
                                                <div className="text-sm text-muted-foreground">
                                                    {getSymbolFromCurrency(
                                                        shop?.base_currency ||
                                                            "USD"
                                                    )}
                                                    1.00 ={" "}
                                                    {getSymbolFromCurrency(
                                                        selectedMarketCurrencyCode
                                                    )}
                                                    {exchangeRates[
                                                        selectedMarketCurrencyCode
                                                    ]?.toFixed(2)}
                                                </div>
                                            </div>
                                        )}
                                    </h3>
                                </div>
                                <CurrencyProvider
                                    currencyCode={selectedMarketCurrencyCode}
                                >
                                    {/* <EditProductFormProvider> */}
                                    <PriceRangeSlider
                                        form={form}
                                        product={product}
                                        shop={shop}
                                        optimizationSettings={
                                            optimizationSettings
                                        }
                                        setOptimizationSettings={
                                            setOptimizationSettings
                                        }
                                        selectedMarket={selectedMarket}
                                        recalculateFormValues={
                                            recalculateFormValues
                                        }
                                        productPrice={
                                            productPriceInSelectedMarketCurrency
                                        }
                                    />
                                    <CostSlider
                                        form={form}
                                        product={product}
                                        shop={shop}
                                        selectedMarket={selectedMarket}
                                        optimizationSettings={
                                            optimizationSettings
                                        }
                                        setOptimizationSettings={
                                            setOptimizationSettings
                                        }
                                        recalculateFormValues={
                                            recalculateFormValues
                                        }
                                        productPrice={
                                            productPriceInSelectedMarketCurrency
                                        }
                                    />
                                    <PriceRoundSelect
                                        form={form}
                                        selectedMarket={selectedMarket}
                                        optimizationSettings={
                                            optimizationSettings
                                        }
                                        setOptimizationSettings={
                                            setOptimizationSettings
                                        }
                                        recalculateFormValues={
                                            recalculateFormValues
                                        }
                                    />
                                    {/* <OptimizationIntervalSelect form={form} /> */}
                                    {/* <TimezoneSelect form={form} /> */}
                                    <MetricSelect form={form} />
                                </CurrencyProvider>
                            </div>
                        </div>
                        <DialogFooter className="flex items-center justify-between">
                            <div className="mr-auto flex items-center gap-2">
                                <div className="flex items-center gap-2">
                                    <Switch
                                        checked={
                                            enabledMarkets.length ===
                                            markets.length
                                        }
                                        onCheckedChange={(checked) => {
                                            setEnabledMarkets(
                                                checked
                                                    ? markets.map(
                                                          (m) => m.handle
                                                      )
                                                    : []
                                            );
                                        }}
                                        disabled={isOptimizationUnderway}
                                        className={
                                            enabledMarkets.length > 0 &&
                                            enabledMarkets.length <
                                                markets.length
                                                ? "data-[state=checked]:bg-primary/50"
                                                : ""
                                        }
                                    />
                                    <span className="text-sm text-muted-foreground">
                                        {enabledMarkets.length ===
                                        markets.length
                                            ? "All markets enabled"
                                            : enabledMarkets.length === 0
                                            ? "No markets enabled"
                                            : `${enabledMarkets.length} of ${markets.length} markets enabled`}
                                    </span>
                                </div>
                            </div>
                            <div className="flex gap-2">
                                <Button
                                    type="button"
                                    variant="outline"
                                    onClick={undoChanges}
                                    disabled={
                                        !formHasChanged() ||
                                        isOptimizationUnderway
                                    }
                                >
                                    Undo changes
                                </Button>
                                <Button
                                    onClick={() => onSubmit(form.getValues())}
                                    disabled={
                                        isSubmitting ||
                                        !formHasChanged() ||
                                        isOptimizationUnderway
                                    }
                                    className="w-[120px]"
                                >
                                    <LoadingButtonContent
                                        loading={isSubmitting}
                                    >
                                        {isOptimizationUnderway ? (
                                            <div className="flex items-center gap-2">
                                                <Lock className="h-4 w-4" />
                                                <span>Locked</span>
                                            </div>
                                        ) : (
                                            "Save changes"
                                        )}
                                    </LoadingButtonContent>
                                </Button>
                            </div>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    );
}
